<template>
    <b-modal
        id="newPatientModal"
        ref="newPatientModal"
        :lazy="true"
        :scrollable="enableRecordSharing ? false : true"
        :no-close-on-backdrop="true"
        :no-close-on-esc="enableRecordSharing ? true : false"
        title="New Patient"
        title-tag="h2"
        content-class="overflow-visible"
        @shown="onModalShown"
        :hide-backdrop="hideBackdrop"
    >
        <b-alert :show="isReachQuota" variant="danger">
            <i class="cus-exclamation"></i>
            <span class="text-danger ml-1">{{ reachQuotaMessage }}</span>
        </b-alert>
        <form>
            <input v-model="data.client_id" type="hidden" name="client_id" />
            <div class="form-group row name-holder">
                <label for="name" class="col-sm-4 col-form-label">Name:</label>
                <div class="col">
                    <suggest
                        v-if="enableRecordSharing"
                        display-attribute="name"
                        value-attribute="id"
                        @select="applySelectRecordSharing"
                        :list="getPatientNameSuggestions"
                        :max-suggestions="0"
                        :min-length="0"
                        :debounce="300"
                        :styles="{
                            vueSimpleSuggest: 'patient-record-sharing',
                            suggestItem: 'suggest-item-vetter',
                            suggestions: 'suggestions-rs'
                        }"
                    >
                        <input
                            id="name"
                            ref="patientName"
                            v-model="data.name"
                            v-validate="'required'"
                            :class="{ 'border-danger': errors.has('name') }"
                            type="text"
                            class="form-control"
                            name="name"
                            placeholder="Patient Name (required)"
                            @blur="checkSharedInput()"
                        />

                        <div slot="suggestion-item" slot-scope="{ suggestion }">
                            <p class="name">
                                <span>{{ suggestion.name }}</span>
                                <span v-if="validOwner(suggestion.owner)">
                                    ({{ suggestion.owner.last_name }},
                                    {{ suggestion.owner.first_name }})
                                </span>
                            </p>
                            <p>
                                {{ suggestion.breeds[0].label }} ({{
                                    suggestion.species.label
                                }})
                            </p>
                            <p>
                                {{ suggestion.sex.label }}
                            </p>
                        </div>
                    </suggest>
                    <input
                        v-else
                        ref="patientName"
                        id="name"
                        v-model="data.name"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('name') }"
                        type="text"
                        class="form-control"
                        name="name"
                        placeholder="Patient Name (required)"
                    />
                </div>
            </div>
            <div class="form-group row">
                <label for="status" class="col-sm-4 col-form-label"
                    >Status:</label
                >
                <div class="col">
                    <select
                        id="status"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('status') }"
                        v-model="data.status"
                        name="status"
                        class="form-control"
                        aria-invalid="false"
                    >
                        <option value="1">Active</option>
                        <option value="3">Deceased</option>
                        <option value="2">Inactive</option>
                    </select>
                </div>
            </div>
            <div class="form-group row">
                <label for="reminder" class="col-sm-4 col-form-label"
                    >Breed:</label
                >
                <div class="col">
                    <v-select
                        id="breed"
                        ref="breed"
                        v-model="data.breeds"
                        :class="{ 'border-danger': errors.has('breed') }"
                        :options="breed_options_exclude"
                        @search="searchBreeds"
                        v-validate="'required'"
                        @input="breedSelected"
                        name="breed"
                        placeholder="Breed (required)"
                        label="breedSpecies"
                        multiple
                    >
                        <template slot="no-options"
                            >Please enter 3 or more characters</template
                        >
                    </v-select>
                </div>
            </div>
            <div class="form-group row">
                <label for="color" class="col-sm-4 col-form-label"
                    >Color:</label
                >
                <div class="col">
                    <input
                        id="color"
                        v-model="data.color"
                        :class="{ 'border-danger': errors.has('color') }"
                        v-validate="''"
                        type="text"
                        class="form-control"
                        name="color"
                        placeholder="Color (optional)"
                    />
                </div>
            </div>
            <div class="form-group row">
                <label for="sex" class="col-sm-4 col-form-label">Sex:</label>
                <div class="col">
                    <select
                        id="sex"
                        :class="{ 'border-danger': errors.has('sex') }"
                        v-validate="'required'"
                        v-model="data.sex"
                        name="sex"
                        class="form-control"
                        aria-invalid="false"
                    >
                        <option
                            v-for="value in sexOptions"
                            :key="value.id"
                            :value="value.id"
                            >{{ value.label }}</option
                        >
                    </select>
                </div>
            </div>
            <div class="form-group row">
                <label for="agey" class="col-sm-4 col-form-label">Age:</label>
                <div class="col row ml-0 mr-1">
                    <input
                        id="agey"
                        v-model="data.year"
                        v-validate="'numeric'"
                        maxlength="3"
                        :class="{ 'border-danger': errors.has('agey') }"
                        type="text"
                        class="form-control col-2"
                        name="agey"
                        placeholder="Years"
                        @input="ageChanged()"
                    />
                    <span class="pt-2 pl-2">yrs</span>
                    <input
                        id="agem"
                        v-model="data.month"
                        v-validate="'numeric'"
                        maxlength="4"
                        :class="{ 'border-danger': errors.has('agem') }"
                        type="text"
                        class="form-control col-2 ml-2"
                        name="agem"
                        placeholder="Months"
                        @input="ageChanged()"
                    />
                    <span class="pt-2 pl-2">mos</span>
                    <input
                        id="agew"
                        v-model="data.week"
                        v-validate="'numeric'"
                        maxlength="4"
                        :class="{ 'border-danger': errors.has('agew') }"
                        type="text"
                        class="form-control col-2 ml-2"
                        name="agew"
                        placeholder="Weeks"
                        @input="ageChanged()"
                    />
                    <span class="pt-2 pl-2">wks</span>
                </div>
            </div>
            <div class="form-group row">
                <label for="birthdate" class="col-sm-4 col-form-label"
                    >Birthdate:</label
                >
                <div class="col">
                    <date-picker
                        :config="datePickerOptions"
                        v-model="data.birthdate"
                        v-validate="''"
                        @dp-show="_datePickerShow"
                        class="form-control"
                        :placeholder="getDateFormat() + ' (optional)'"
                        name="birthdate"
                    ></date-picker>
                </div>
            </div>
            <div class="form-group row">
                <label for="chip" class="col-sm-4 col-form-label">Chip:</label>
                <div class="col">
                    <input
                        id="chip"
                        v-model="data.chip"
                        type="chip"
                        class="form-control"
                        placeholder="Chip number (optional)"
                    />
                </div>
            </div>
            <div class="form-group row">
                <label for="primary-provider" class="col-sm-4 col-form-label"
                    >Primary Provider:</label
                >
                <div class="col">
                    <v-select
                        id="primary-provider"
                        ref="vselectProvider"
                        v-model="data.primary_provider_id"
                        name="provider"
                        aria-placeholder="true"
                        placeholder="Primary Provider"
                        :options="providers"
                        :clearable="false"
                        :reduce="provider => provider.id"
                    ></v-select>
                </div>
            </div>
        </form>
        <div v-if="isSaving" slot="modal-footer" class="w-100">
            <div class="float-right">
                <loader :margin="1" />
            </div>
        </div>
        <div v-else slot="modal-footer" class="w-100">
            <b-button
                @click="$refs.newPatientModal.hide()"
                class="float-left"
                variant="secondary"
                >Cancel</b-button
            >
            <div class="float-right">
                <b-button
                    v-if="!showAddNew"
                    @click="save()"
                    variant="primary"
                    :disabled="isReachQuota"
                    >Save</b-button
                >
                <b-button-group v-if="showAddNew">
                    <b-dropdown
                        v-if="!continues"
                        @click="save(false)"
                        left
                        split
                        text="Save + Done"
                        variant="primary"
                        :disabled="isReachQuota"
                    >
                        <b-dropdown-item
                            v-if="!isReachQuota"
                            @click="save(true)"
                            >Save + Add New</b-dropdown-item
                        >
                    </b-dropdown>
                    <b-dropdown
                        v-else
                        @click="save(true)"
                        left
                        split
                        text="Save + Add New"
                        variant="primary"
                        :disabled="isReachQuota"
                    >
                        <b-dropdown-item
                            v-if="!isReachQuota"
                            @click="save(false)"
                            >Save + Done</b-dropdown-item
                        >
                    </b-dropdown>
                </b-button-group>
            </div>
        </div>
    </b-modal>
</template>
<script>
import vSelect from "vue-select";
import Loader from "@/module/commons/loader";
import { assign, sortBy } from "lodash";
import Suggest from "vue-simple-suggest";
import StaffService from "@/services/staff";

const emptyProvider = { id: null, label: "No primary provider" };

export default {
    components: {
        vSelect,
        Loader,
        Suggest
    },
    props: {
        showAddNew: {
            type: Boolean,
            default: false
        },
        enableRecordSharing: {
            type: Boolean,
            default: false // true = show suggestion patient name
        },
        clientId: {
            type: String,
            default: null
        },
        hideBackdrop: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            resources: {},
            data: {
                name: "",
                client_id: null,
                status: 1,
                year: null,
                month: null,
                week: null,
                birthdate: null,
                sex: "",
                chip: "",
                breeds: [],
                active: 1,
                color: null,
                primary_provider_id: null
            },
            patient_data: {},
            datePickerOptions: {
                useCurrent: false,
                widgetParent: "#newPatientModal"
            },
            breedOptions: [],
            sexOptions: [],
            isSaving: false,
            continues: false,
            isReachQuota: false,
            reachQuotaMessage: null,
            recordSharedId: null,
            recordSharedName: null,
            providers: [emptyProvider]
        };
    },
    computed: {
        breed_options_exclude() {
            return _.differenceWith(
                this.breedOptions,
                this.data.breeds,
                (breedA, breedB) => breedA.id == breedB.id
            );
        }
    },
    methods: {
        open(data) {
            this.data = _.merge(this.data, data);
            this.checkSubscriptionLimit();
            this.getProviders();
            this.$refs.newPatientModal.show();
        },
        onModalShown() {
            this.$refs.patientName.focus();
        },
        async applySelectRecordSharing(value) {
            this.recordSharedId = value.id;
            this.recordSharedName = value.name;
            this.data.name = value.name;
            this.data.active = value.status ? value.status.id : 1;
            this.data.color = value.color;
            this.data.sex = value.sex ? value.sex.id : "";
            this.data.chip = value.chip;

            if (value.species) {
                await this.searchBreedsResource({
                    query: "",
                    species: value.species.id
                });

                let species = [];

                for (const breed of value.breeds) {
                    let selected = this.breedOptions.find(
                        b => b.id === breed.id
                    );
                    species.push(selected);
                }

                this.data.breeds = species;
                await this.getSex(value.species.id);
            } else {
                this.data.breeds = [];
            }

            // age
            let age = value.age || "";
            let splitAge = age.trim().split(" ");

            for (let n = 0; n < splitAge.length; n++) {
                let label = splitAge[n];
                let value = splitAge[n - 1] || "";
                switch (label) {
                    case "weeks":
                    case "week":
                        this.data.week = value;
                        break;
                    case "year":
                    case "years":
                        this.data.year = value;
                        break;
                    case "months":
                    case "month":
                        this.data.month = value;
                        break;
                }
            }

            // birthdate
            if (value.birthdate && value.birthdate !== "") {
                let dt = this._toDateTime(value.birthdate);
                this.data.birthdate = dt.toFormat(this._dateFormat);
            } else {
                this.data.birthdate = "";
            }
        },
        validOwner(owner) {
            if (!owner) {
                return false;
            }

            if (Array.isArray(owner)) {
                return false;
            }

            return true;
        },
        async getPatientNameSuggestions(query = "") {
            if (query.length < 2 && !this.clientId) {
                return [];
            }

            const res = await this.$http.get(
                `${process.env.BARRAMUNDI}patient/search-patient-in-groups`,
                {
                    params: {
                        query: query,
                        clientId: this.clientId
                    }
                }
            );

            if (res.data && res.data.response && res.data.response.resources) {
                return res.data.response.resources;
            } else {
                return [];
            }
        },
        checkSubscriptionLimit() {
            this.isReachQuota = false;
            this.reachQuotaMessage = "";

            this._getSubscription().then(data => {
                if (Number(data.patient_status) === 1) {
                    this.isReachQuota = true;
                    this.reachQuotaMessage = data.patient_message;
                }
            });
        },
        async save(continues) {
            const isValid = await this.$validator.validateAll();

            if (!isValid) {
                return false;
            }

            this.data.breed = _.map(this.data.breeds, "id").join(",");

            if (this.data.breeds) {
                this.data.species = this.data.breeds[0].species_id;
            }

            this.isSaving = true;

            if (this.recordSharedId) {
                this.data.group_insert = true;
                const resShared = await this.$http.put(
                    `${process.env.BARRAMUNDI}patient/${this.recordSharedId}`,
                    this.data
                );

                this.isSaving = false;

                this.patient_data = resShared.data.response.resource;
                this._message("Patient successfully added", "success");
            } else {
                const res = await this.$http.post(
                    `${process.env.BARRAMUNDI}patient`,
                    this.data
                );

                this.isSaving = false;

                this.patient_data = res.data.response.resource;
                this._message("Patient successfully added", "success");
            }

            this.$validator.reset();
            if (continues) {
                assign(this.$data, this.$options.data());
                this.continues = continues;
                this.open();
            } else {
                this.patient_data.fullname = this.patient_data.name; // append "fullname" key. New Relationship (client) uses 'fullname' key.
                this.$emit("saved", this.patient_data);
                assign(this.$data, this.$options.data());
                this.$refs.newPatientModal.hide();
            }
        },
        searchBreeds(search) {
            let params = { query: search };
            let self = this;

            if (!search) {
                self.breedOptions = [];
            }

            if (self.data.breeds.length && self.breedOptions.length) {
                return (params.species = self.data.breeds[0].species_id);
            }

            if (search && search.length > 2) {
                if (self.data.breeds.length > 0) {
                    params.species = self.data.breeds[0].species_id;
                }
                return this.searchBreedsResource(params);
            }
        },
        breedSelected(event) {
            let self = this;
            if (event && event.length < 1) {
                self.breedOptions = [];
                self.sexOptions = [];
                self.data.sex = "";
                return;
            }

            if (self.data.breeds.length > 1 && self.breedOptions.length) {
                return;
            }

            if (event.length > 0) {
                self.getSex(event[0].species_id);
            }

            let params = { query: "", species: event[0].species_id };
            return this.searchBreedsResource(params);
        },
        async searchBreedsResource(params) {
            let self = this;
            let response = await this.$http.get(
                process.env.PATIENT_SERVICE + "breed-search",
                {
                    params
                }
            );

            if (response) {
                self.breedOptions = response.data.response.resources;
                self.breedOptions.forEach(element => {
                    element["breedSpecies"] =
                        element.name +
                        " (" +
                        _.get(element, "species_name", "").toLowerCase() +
                        ")";
                });
            } else {
                self._message(response.message.errors, "danger");
            }
            return response;
        },
        async getSex(speciesId) {
            let self = this;
            let response = await this.$http.get(
                process.env.BARRAMUNDI + "patient/sex/" + speciesId
            );

            if (response) {
                self.sexOptions = response.data.response.resource.data || [];
                if (self.data.sex == "") {
                    self.data.sex = self.sexOptions[0].id;
                }
            }
        },
        ageChanged() {
            this.data.birthdate = null;
        },
        checkSharedInput() {
            if (
                this.enableRecordSharing &&
                this.recordSharedId &&
                this.recordSharedName
            ) {
                if (this.recordSharedName != this.data.name) {
                    this.recordSharedId = null;
                    this.recordSharedName = null;
                    this.data.breeds = [];
                    this.breedOptions = [];
                    this.data.status = 1;
                    this.data.year = null;
                    this.data.month = null;
                    this.data.week = null;
                    this.data.birthdate = null;
                    this.data.chip = "";
                    this.data.breeds = [];
                    this.data.active = 1;
                    this.data.color = null;
                    this.$validator.reset();
                }
            }
        },
        getProviders() {
            let self = this;

            StaffService.getProviders().then(response => {
                if (response.resource && response.resource.data) {
                    let providers = response.resource.data.map(provider => {
                        return provider.actor;
                    });
                    self.providers.push(...sortBy(providers, ["label"]));
                }
            });
        }
    }
};
</script>

<style scoped>
::v-deep .suggest-item-vetter {
    padding: 10px 15px !important;
}
.suggest-item-vetter p {
    margin: 0;
    padding: 0;
}
.suggest-item-vetter p.name {
    font-weight: bold;
    color: #000;
}
.suggest-item-vetter.hover p.name,
.suggest-item-vetter.selected p.name {
    color: #fff;
}
::v-deep .suggestions-rs {
    box-shadow: 0 5px 10px rgb(0 0 0 / 20%);
    border: 1px solid #0003 !important;
    border-radius: 5px !important;
}
::v-deep
    .vue-simple-suggest.designed
    .suggestions.suggestions-rs
    .suggest-item.hover {
    border-radius: 0;
    background-color: #0097cf !important;
}
::v-deep
    .vue-simple-suggest.designed
    .suggestions.suggestions-rs
    .suggest-item.selected {
    background-color: #05a1da !important;
}
::v-deep .suggestions-rs .suggest-item-vetter:first-child.hover {
    border-top-left-radius: 5px !important;
    border-top-right-radius: 5px !important;
}
::v-deep .suggestions-rs .suggest-item-vetter:last-child.hover {
    border-bottom-left-radius: 5px !important;
    border-bottom-right-radius: 5px !important;
}

::v-deep .name-holder .vue-simple-suggest ul.suggestions {
    max-height: 500px;
    overflow: scroll;
    margin-top: 30px;
}
</style>
